import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { PageLoading } from "../../../../components";
import { usePrimaryOrganizationId } from "../../../../hooks";
import { useEmploymentCollectionGroups } from "../../../../hooks/useCollectionGroups";
import {
  DataFlowsSourcesFooterState,
  DataFlowsSourcesPageConfig,
  DataFlowsSourcesPageContent,
} from "../../../data-map/data-flows/DataFlowsSourcesPage";
import { Routes } from "../../../../root/routes";
import {
  EMPLOYEE_EXCLUDED_KEYS,
  EMPLOYEE_OTHER_EXCLUDED_CATEGORIES,
} from "../../../../surveys/employeeSourcesSurvey";
import {
  SOURCE_KEY_CONSUMER_DIRECTLY,
  VENDOR_CATEGORY_APPLICANT_TRACKING,
  VENDOR_CATEGORY_BACKGROUND_CHECK_PROVIDER,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_CATEGORY_RECRUITMENT_SERVICE,
} from "../../../../types/Vendor";
import { lexicalJoin } from "../../../../util/text";
import { EmployeePageWrapper } from "./EmployeePageWrapper";

const EMPLOYEE_SOURCES_SURVEY_NAME = "employee-sources-survey";
const WORKER_DIRECTLY_SLUG = "worker-directly";

export function EmployeeSourcesPage() {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const [employmentGroups, groupsRequest] = useEmploymentCollectionGroups(orgId);
  const [footerState, setFooterState] = useState<DataFlowsSourcesFooterState>({
    isComplete: false,
    isCompleteTooltip: null,
  });

  const groupLabel = employmentGroups.length
    ? lexicalJoin(employmentGroups.map((group) => group.name))
    : "your employment groups";

  const config = useMemo<DataFlowsSourcesPageConfig>(
    () => ({
      collectionContext: "EMPLOYEE",
      surveyName: EMPLOYEE_SOURCES_SURVEY_NAME,
      primarySource: {
        vendorKey: SOURCE_KEY_CONSUMER_DIRECTLY,
        surveySlug: WORKER_DIRECTLY_SLUG,
        surveyQuestion: `Does your business collect personal data — such as contact information, resumes, I-9 forms, or bank account information — directly from ${groupLabel}?`,
        sourceName: "Worker",
        sectionTitle: "Collection Groups",
        sectionDescription:
          "A worker, or another employment collection group, is usually the primary source of employment data for a business.",
        recommendation:
          "We recommend adding “Worker” as a source. It is very uncommon that a business does not collect data from its employees, contractors, or job applicants either directly or indirectly.",
        preselectCollectedPics: false,
      },
      categorySources: [
        {
          vendorCategories: [VENDOR_CATEGORY_DATA_BROKER],
          sectionTitle: "Data Brokers",
          sectionDescription: `Add data brokers that your business collects personal information about ${groupLabel} from. Data brokers are third parties that aggregate personal data about people they do not have a direct relationship with and sell it to businesses.`,
          addSourceButtonLabel: "Add Data Broker Source",
          modalTitle: "Add Data Broker Source",
        },
        {
          vendorCategories: [
            VENDOR_CATEGORY_APPLICANT_TRACKING,
            VENDOR_CATEGORY_RECRUITMENT_SERVICE,
          ],
          sectionTitle: "Applicant Sourcing Entities",
          sectionDescription: `Add employment agencies, recruiters, applicant tracking systems, or other entities that your business acquires data about ${groupLabel} from.`,
          addSourceButtonLabel: "Add Applicant Sourcing Entity",
          modalTitle: "Add Applicant Sourcing Entity",
        },
        {
          vendorCategories: [VENDOR_CATEGORY_BACKGROUND_CHECK_PROVIDER],
          sectionTitle: "Background Check Providers",
          sectionDescription: `Add background check providers that your business collects personal data about ${groupLabel} from.`,
          addSourceButtonLabel: "Add Background Check Provider",
          modalTitle: "Add Background Check Provider",
        },
      ],
      otherSources: {
        excludedCategories: EMPLOYEE_OTHER_EXCLUDED_CATEGORIES,
        excludedVendorKeys: EMPLOYEE_EXCLUDED_KEYS,
        sectionDescription:
          "Add any other sources that your business receives employment-related personal information from.",
      },
      showReferrals: false,
      isHr: true,
      includeAllContextsInExistingSources: false,
      addKnownSourcesWithExplicitContext: true,
    }),
    [groupLabel],
  );

  if (groupsRequest.running) {
    return <PageLoading />;
  }

  return (
    <EmployeePageWrapper
      selectedTabKey="employee-sources"
      nextOnClick={() => history.push(Routes.hrCenter.hrVendorDocumentation)}
      nextContent="Done with Sources"
      nextDisabled={!footerState.isComplete}
      nextDisabledTooltip={footerState.isCompleteTooltip ?? undefined}
    >
      <div className="tw-boundary">
        <DataFlowsSourcesPageContent
          config={config}
          collectionGroups={employmentGroups}
          onFooterStateChange={setFooterState}
        />
      </div>
    </EmployeePageWrapper>
  );
}
